import type { MarketingFrame, SectionId } from '../types';
import { sampleContentAwarePin } from '../score';
import { headingReadBounds, headingRestPoint } from './headingReadBounds';
import {sampleFreelance} from './freelanceScore';
import { NarrativeClock, sampleNarrative, sampleCloser, samplePlans, samplePlanInspection, sampleInspection, inspectionPoint, sampleFinale, canAdvanceNarrative, ease } from './sequence';
import { CHAPTERS } from '../cinema/transport';

const IDS: SectionId[] = ['uses-video','uses-freelance','uses-more','share','audience','closer','pricing','install','footer'];

/*
 * Авторская длительность раздела — та же, по которой его ведёт режиссёр кино.
 * Берётся из партитуры глав, а не переписывается здесь: иначе на телефоне и на
 * компьютере разделы шли бы по разным часам, и расхождение никто бы не заметил.
 */
const CHAPTER_MS = new Map<SectionId, number>(
  CHAPTERS.filter((chapter): chapter is typeof chapter & { id: SectionId } => (IDS as string[]).includes(chapter.id))
    .map((chapter) => [chapter.id, chapter.duration]),
);
const boxOf = (n: Element | null) => n?.getBoundingClientRect();

/** DOM and the existing Three world consume these same screen-space cues. */
export class NarrativeDirector {
  private clocks = new Map<SectionId,NarrativeClock>();
  private nodes = new Map<SectionId,HTMLElement>();
  private pricingCompanion: {x:number;y:number;at:number;visit:number}|null=null;
  constructor(private root: HTMLElement) {
    if (typeof root?.querySelector !== 'function') return;
    for (const id of IDS) {
      const node = root.querySelector<HTMLElement>(`#${id}`); if(!node?.dataset) continue;
      this.nodes.set(id,node);this.clocks.set(id,new NarrativeClock());
      node.dataset.narrativeDirected='true';
    }
  }
  render(frame: MarketingFrame, now: number): void {
    const view=this.root.ownerDocument?.defaultView;
    if (!view) return;
    const h=view.innerHeight,w=view.innerWidth;
    for (const [id,node] of this.nodes) {
      const inner=node.querySelector<HTMLElement>(':scope > .marketing-inner');
      if (!inner) continue;
      if (id==='audience'||id==='pricing'||id==='install'||id==='footer') {
        const offset=sampleContentAwarePin(frame.travelPx-node.offsetTop,node.offsetHeight,h,inner.offsetHeight);
        node.style.setProperty('--block-pin-y',`${offset.toFixed(2)}px`);
      }
      const box=boxOf(inner);
      if (!box) continue;
      const active=frame.handoff>.99 && frame.activeSection===id;
      const titleNode=node.querySelector(id==='closer'?'[data-closer-line="0"]':'h2');
      const title=boxOf(titleNode);
      const clock=this.clocks.get(id)!;
      const visible=canAdvanceNarrative(active,node.dataset.blockHeld!=='false',box,title,h,clock.elapsed>0);
      const before=node.getBoundingClientRect().top>h*1.25;
      const cinema=view.__BB_CINEMA__;
      const directed=cinema?.active&&cinema.chapter.id===id;
      /*
       * Три источника времени раздела, и только один действует в каждом случае.
       *
       *   режиссёр    — глава идёт по его часам (компьютер);
       *   прокрутка   — телефон: ход раздела считается из его собственной
       *                 прокрутки, `ms = прогресс × длительность`. Настенные
       *                 часы здесь не годятся: раздел держится ровно столько,
       *                 сколько человек его листает, а не 4–17 секунд, и
       *                 содержание успевало бы показаться только тому, кто
       *                 остановился. Замерено: карточки тарифов появляются на
       *                 4300 мс, причины «Поделиться» — на 4100 мс, а быстрый
       *                 пролёт удержания занимает около секунды.
       *   свои часы   — всё остальное: раздел идёт, пока виден.
       */
      const scrubbed=!directed&&frame.viewport==='mobile';
      const ms=directed?cinema.state.elapsed
        :scrubbed?frame.sections[id].progress*(CHAPTER_MS.get(id)??0)
          :clock.tick(now,visible,before), reduced=frame.motion==='reduced';
      const cue=sampleNarrative(ms,reduced), time=reduced?1e6:ms;
      node.dataset.narrativeStage=cue.stage;
      node.dataset.narrativeElapsed=String(Math.round(ms));
      node.style.setProperty('--narrative-heading',String(cue.heading));
      node.style.setProperty('--narrative-mask',`${100*(1-cue.heading)}%`);
      node.style.setProperty('--narrative-content',String(cue.content));
      node.style.setProperty('--narrative-subtitle',String(ease((time-4700)/800)));
      let read=cue.reading;
      let readTitle=titleNode;
      if(id==='closer') {
        const score=sampleCloser(time,reduced);
        node.dataset.closerActiveLine=time>=5600?'1':'0';
        node.style.setProperty('--closer-first',String(score.first));node.style.setProperty('--closer-second',String(score.second));
        node.dataset.closerEmphasis=String(score.emphasis);read=score.reading;
      }
      if(id==='share'){
        node.dataset.shareElapsedMs=String(ms);
        node.dataset.shareExitProgress=String(directed?(cinema.transitioning?Math.min(1,cinema.transitionProgress/cinema.departureHold):0):ease((frame.sections.share.progress-.86)/.14));
        for(const [i,item] of [...node.querySelectorAll<HTMLElement>('.share-reasons li')].entries())item.style.setProperty('--reason-presence',String(ease((time-4100-i*650)/650)));
      }
      if(id==='audience')for(const [i,item] of [...node.querySelectorAll<HTMLElement>('[data-audience-point]')].entries()) {
        const row=Number(item.dataset.audiencePoint??i);
        item.style.setProperty('--argument-presence',String(ease((time-3900-row*950)/800)));
      }
      if(id==='uses-freelance'){
        const work=sampleFreelance(time,reduced);
        for(const name of ['towers','deck','cables','materials','flight'] as const)node.dataset[`freelance${name[0]!.toUpperCase()}${name.slice(1)}`]=String(work[name]);
        node.dataset.freelanceComplete=String(work.complete);
        node.style.setProperty('--commission-ending',String(work.ending));
        for(const [i,item] of [...node.querySelectorAll<HTMLElement>('[data-freelance-step]')].entries())item.style.setProperty('--work-presence',String(work.theses[i]??1));
      }
      if(id==='pricing'){
        const p=samplePlans(ms,reduced),host=node.querySelector<HTMLElement>('[data-offer-pricing]')??node;
        const track=host.querySelector<HTMLElement>('.offer-pricing__grid');
        for(const [i,card] of [...host.querySelectorAll<HTMLElement>('[data-offer-plan]')].entries())card.style.setProperty('--offer-plan-presence',String(reduced?1:ease((time-4300-i*1600)/650)));
        if(track&&w<=1000){
          track.tabIndex=0;track.setAttribute('aria-label','Тарифы. Проведи в сторону, чтобы сравнить.');
          if(directed){
            const visit=String(cinema.state.visit);
            if(track.dataset.offerVisit!==visit){
              track.dataset.offerVisit=visit;
              track.scrollLeft=cinema.state.phase==='ready'||reduced?track.scrollWidth-track.clientWidth:0;
            }
          }
          if(directed&&cinema.state.phase==='playing'&&!reduced){
            const cards=[...track.querySelectorAll<HTMLElement>('[data-offer-plan]')],inspection=samplePlanInspection(ms);
            const x=(index:number)=>Math.max(0,Math.min(track.scrollWidth-track.clientWidth,(cards[Math.max(0,index)]?.offsetLeft??0)-track.offsetLeft));
            track.scrollLeft=x(inspection.from)+(x(inspection.to)-x(inspection.from))*inspection.transfer;
          }
        }
        host.dataset.offerDirected='true';host.dataset.offerReveal=String(p.reveal);host.dataset.offerInspect=String(p.inspect);
        host.dataset.offerYearStage=p.year;host.dataset.offerTrial=String(p.trial);
      }
      if(id==='install'){
        const host=node.querySelector<HTMLElement>('[data-offer-install]')??node;
        host.dataset.offerDirected='true';host.dataset.offerReveal=time<4000?'0':time<5100?'1':time<6200?'2':'3';
        host.dataset.offerInspect=String(reduced?-1:sampleInspection(ms,2,5500,2000,650).inspect);
      }
      if(id==='footer') {
        const finale=sampleFinale(frame.sections.footer.progress,active&&box.top<h&&box.bottom>0,reduced);
        node.dataset.finaleProgress=String(finale.progress);
        node.style.setProperty('--finale-footer',String(finale.footer));
      }
      if(!active)continue;
      const titleBox=headingReadBounds(readTitle);
      if(!titleBox)continue;
      const inset=Math.min(35,titleBox.width*.07), readWidth=titleBox.width;
      let x=titleBox.x+(titleBox.width-readWidth)/2+inset+(readWidth-inset*2)*read;
      const cubeSize=w<700?36:Math.min(54,Math.max(38,h*.065));
      // The rotated mesh projects larger than its nominal edge size.
      let y=titleBox.bottom+cubeSize;
      if(id==='closer'){
        const score=sampleCloser(time,reduced),firstPark=headingRestPoint(titleBox,w,h);
        const second=headingReadBounds(node.querySelector('[data-closer-line="1"] .closer-statement'));
        const secondPark=second?headingRestPoint(second,w,h):firstPark;
        x+=(firstPark.x-x)*score.park;y+=(firstPark.y-y)*score.park;
        x+=(secondPark.x-x)*score.handoff;y+=(secondPark.y-y)*score.handoff;
      }
      if(time>3800 && id!=='closer') {
        const park=headingRestPoint(titleBox,w,h);
        let target=park;
        if(id==='pricing'){
          const lift=20*ease((time-10000)/700);
          const cards=[...node.querySelectorAll('[data-offer-plan]')].map((card,index)=>{
            const r=card.getBoundingClientRect();return {x:r.x+r.width/2,y:r.top-cubeSize-12-(index===3?lift:0)};
          });
          const firstEnd={x:titleBox.right-inset,y:titleBox.bottom+cubeSize};
          target=inspectionPoint(samplePlanInspection(time),cards,firstEnd);
          if(time>10700){
            const host=node.querySelector<HTMLElement>('[data-offer-pricing]');
            const chosen=host?.dataset.offerHover??host?.dataset.offerSelected;
            const terms=['1','3','6','12'],index=chosen?terms.indexOf(chosen):-1;
            if(index>=0)target=cards[index]??target;
            const previous=this.pricingCompanion,visit=cinema?.state.visit??0;
            if(previous&&previous.visit===visit){const response=reduced?1:1-Math.exp(-Math.max(0,now-previous.at)/260);target={x:previous.x+(target.x-previous.x)*response,y:previous.y+(target.y-previous.y)*response};}
            this.pricingCompanion={...target,at:now,visit};
          }else this.pricingCompanion=null;
        }
        if(id==='uses-freelance'){
          const r=boxOf(node.querySelector('[data-freelance-cube-stage]'));
          if(r){
            const travel=(start:number)=>{const t=Math.max(0,Math.min(1,(time-start)/1700));return t*t*(3-2*t);};
            target={x:r.x+r.width*(.18+.14*travel(5700)),y:r.y+r.height*.14};
          }
        }
        if(id==='uses-more'){
          const r=boxOf(node.querySelector('.creative-capabilities__choices'));
          if(r)target={x:r.right+38,y:r.top+28};
        }
        const t=ease((time-3800)/900);x+=(target.x-x)*t;y+=(target.y-y)*t;
      }
      node.dataset.narrativeCubeX=String(x);node.dataset.narrativeCubeY=String(y);
      node.dataset.narrativeCubeSize=String(cubeSize);
    }
  }
  destroy():void{for(const node of this.nodes.values()){
    delete node.dataset.narrativeDirected;
    delete node.dataset.finaleProgress;
    node.style.removeProperty('--finale-footer');
  }}
}
